import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  MessageSquare,
  Sparkles,
  History,
  ChevronRight,
  Database,
  AlertTriangle,
  Plane,
  Users,
  Shield,
  FileText,
  Wrench,
  Loader2,
  Send,
  Clock,
  Trash2,
  RefreshCw,
  Lightbulb
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getCategoryIcon = (category) => {
  const icons = {
    'Incidents & Hazards': AlertTriangle,
    'Flight Data (FDM/FOQA)': Plane,
    'Crew & Fatigue': Users,
    'Audits & Compliance': Shield,
    'Risk Analysis': FileText,
    'Maintenance': Wrench
  };
  return icons[category] || Search;
};

const getCategoryColor = (category) => {
  const colors = {
    'Incidents & Hazards': 'text-red-500',
    'Flight Data (FDM/FOQA)': 'text-blue-500',
    'Crew & Fatigue': 'text-purple-500',
    'Audits & Compliance': 'text-green-500',
    'Risk Analysis': 'text-orange-500',
    'Maintenance': 'text-yellow-500'
  };
  return colors[category] || 'text-gray-500';
};

const getCollectionBadgeColor = (collection) => {
  const colors = {
    incidents: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    fdm_flights: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    crew_members: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
    iosa_full_findings: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    risks: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
    maintenance_tasks: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
  };
  return colors[collection] || 'bg-gray-100 text-gray-700';
};

export default function AIQueryPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [queryResult, setQueryResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('query');
  const token = localStorage.getItem('token');

  const fetchSuggestions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-query/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-query/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchSuggestions();
    fetchHistory();
  }, [fetchSuggestions, fetchHistory]);

  const executeQuery = async (queryText) => {
    if (!queryText.trim()) {
      toast.error('Please enter a query');
      return;
    }

    setIsLoading(true);
    setQueryResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/ai-query/execute`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: queryText })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Query execution failed');
      }

      const data = await response.json();
      setQueryResult(data);
      fetchHistory();
      toast.success(`Found ${data.result_count} results`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    executeQuery(query);
  };

  const handleSuggestionClick = (suggestionText) => {
    setQuery(suggestionText);
    executeQuery(suggestionText);
  };

  const clearHistory = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-query/history`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setHistory([]);
        toast.success('History cleared');
      }
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const renderResultValue = (value) => {
    if (value === null || value === undefined) return <span className="text-gray-400">-</span>;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent flex items-center gap-3">
              <Sparkles className="h-8 w-8 text-violet-500" />
              {t('aiQuery.title', 'AI Safety Query')}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              {t('aiQuery.subtitle', 'Ask questions in natural language and get instant insights from your safety data')}
            </p>
          </div>
          <Badge variant="outline" className="px-3 py-1 text-sm bg-emerald-50 border-emerald-200 text-emerald-700">
            <Sparkles className="w-3 h-3 mr-1" />
            GPT-5.2 Powered
          </Badge>
        </div>

        {/* Main Search Area */}
        <Card className="border-2 border-violet-100 dark:border-violet-900 shadow-lg">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  data-testid="ai-query-input"
                  placeholder={t('aiQuery.placeholder', 'Ask anything about your safety data... e.g., "Show critical incidents this month"')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg border-2 border-slate-200 focus:border-violet-500 rounded-xl"
                  disabled={isLoading}
                />
              </div>
              <Button
                data-testid="ai-query-submit"
                type="submit"
                size="lg"
                disabled={isLoading || !query.trim()}
                className="px-8 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 rounded-xl"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    {t('aiQuery.search', 'Search')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="query" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              {t('aiQuery.results', 'Results')}
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4" />
              {t('aiQuery.suggestions', 'Suggestions')}
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              {t('aiQuery.history', 'History')}
            </TabsTrigger>
          </TabsList>

          {/* Results Tab */}
          <TabsContent value="query" className="space-y-4">
            {queryResult ? (
              <div className="space-y-4">
                {/* Query Interpretation */}
                <Card className="bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border-violet-200">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-violet-500 mt-1" />
                      <div className="flex-1">
                        <p className="font-medium text-violet-900 dark:text-violet-100">
                          {t('aiQuery.interpretation', 'AI Interpretation')}
                        </p>
                        <p className="text-slate-700 dark:text-slate-300 mt-1">
                          {queryResult.query_interpretation}
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <Badge className={getCollectionBadgeColor(queryResult.collection)}>
                            <Database className="w-3 h-3 mr-1" />
                            {queryResult.collection}
                          </Badge>
                          <Badge variant="outline">
                            {queryResult.result_count} results
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Summary */}
                {queryResult.summary && queryResult.result_count > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="h-5 w-5 text-indigo-500" />
                        {t('aiQuery.summary', 'Summary')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-700 dark:text-slate-300">{queryResult.summary}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Results Table */}
                {queryResult.results.length > 0 ? (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">{t('aiQuery.resultData', 'Results Data')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px]">
                        <div className="space-y-3">
                          {queryResult.results.map((result, idx) => (
                            <div
                              key={idx}
                              className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700"
                            >
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {Object.entries(result).slice(0, 12).map(([key, value]) => (
                                  <div key={key} className="text-sm">
                                    <span className="text-slate-500 font-medium">{key}:</span>{' '}
                                    <span className="text-slate-700 dark:text-slate-300">
                                      {renderResultValue(value)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-slate-50 dark:bg-slate-800/30">
                    <CardContent className="py-12 text-center">
                      <Search className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">{t('aiQuery.noResults', 'No results found for your query')}</p>
                      <p className="text-sm text-slate-400 mt-2">
                        {t('aiQuery.tryDifferent', 'Try a different search term or browse the suggestions')}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Follow-up Suggestions */}
                {queryResult.suggestions.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-yellow-500" />
                        {t('aiQuery.followUp', 'Follow-up Suggestions')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {queryResult.suggestions.map((suggestion, idx) => (
                          <Button
                            key={idx}
                            variant="outline"
                            size="sm"
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="hover:bg-violet-50 hover:border-violet-300"
                          >
                            {suggestion}
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card className="bg-slate-50 dark:bg-slate-800/30">
                <CardContent className="py-16 text-center">
                  <Sparkles className="h-16 w-16 mx-auto text-violet-300 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {t('aiQuery.startQuery', 'Ask Your Safety Data')}
                  </h3>
                  <p className="text-slate-500 max-w-md mx-auto">
                    {t('aiQuery.startDescription', 'Type a question in natural language above or choose from the suggestions below to get started.')}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Suggestions Tab */}
          <TabsContent value="suggestions">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suggestions.map((category, idx) => {
                const Icon = getCategoryIcon(category.category);
                const colorClass = getCategoryColor(category.category);
                return (
                  <Card key={idx} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Icon className={`h-5 w-5 ${colorClass}`} />
                        {category.category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {category.examples.map((example, exIdx) => (
                          <button
                            key={exIdx}
                            onClick={() => handleSuggestionClick(example)}
                            className="w-full text-left p-3 text-sm rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-violet-50 dark:hover:bg-violet-900/30 hover:text-violet-700 dark:hover:text-violet-300 transition-colors border border-transparent hover:border-violet-200"
                          >
                            <span className="flex items-center justify-between">
                              {example}
                              <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50" />
                            </span>
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-lg">{t('aiQuery.recentQueries', 'Recent Queries')}</CardTitle>
                  <CardDescription>{t('aiQuery.historyDesc', 'Your recent natural language searches')}</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchHistory}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    {t('common.refresh', 'Refresh')}
                  </Button>
                  {history.length > 0 && (
                    <Button variant="outline" size="sm" onClick={clearHistory}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('aiQuery.clearHistory', 'Clear')}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {history.length > 0 ? (
                  <div className="space-y-2">
                    {history.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(item.query)}
                        className="w-full text-left p-4 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-colors border border-slate-200 dark:border-slate-700"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Search className="h-4 w-4 text-slate-400" />
                            <span className="font-medium text-slate-700 dark:text-slate-300">{item.query}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Badge variant="outline" className="text-xs">
                              {item.result_count || 0} results
                            </Badge>
                            <Clock className="h-3 w-3" />
                            {item.timestamp ? new Date(item.timestamp).toLocaleDateString() : '-'}
                          </div>
                        </div>
                        {item.collection && (
                          <div className="mt-2 ml-7">
                            <Badge className={`text-xs ${getCollectionBadgeColor(item.collection)}`}>
                              {item.collection}
                            </Badge>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <History className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">{t('aiQuery.noHistory', 'No query history yet')}</p>
                    <p className="text-sm text-slate-400 mt-2">
                      {t('aiQuery.historyHint', 'Your searches will appear here')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
